import callGraphQLApi from '@helpers/callGraphQLApi';
import {
  FETCH_APPLICATION_CONFIGS_FAILURE,
  FETCH_APPLICATION_CONFIGS_SUCCESS,
} from './actions';
import { UnknownAction } from '@reduxjs/toolkit';

export interface ApplicationConfigsResponse {
  applicationConfig: {
    /** Total available, which may exceed `records.length` — see the query. */
    count?: number;
    offset?: number;
    limit?: number;
    records: {
      configType: string;
      configKey: string;
      value: string | null;
      valueJSON: Record<string, unknown> | null;
      defaultValue: string | null;
      defaultValueJSON: Record<string, unknown> | null;
    }[];
  };
}

export const queryApplicationConfigsService =
  (applicationId: string) =>
  async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    // `limit` defaults to 30 on the platform (schema: `limit: Int = 30`), and
    // this query passed none — so the app only ever saw the first 30 configs
    // for the application, and anything past that was silently absent. Not an
    // error, not an empty value: the key simply never appeared, and every
    // reader fell through to its default. That is a miserable failure to
    // diagnose from the app, because a missing config and an unregistered one
    // look identical here.
    //
    // 100 is a stopgap chosen to clear the current count with room to spare.
    // It is NOT a fix — it is the same bug with a higher ceiling, and it will
    // bite again.
    //
    // TODO: page this properly. The response carries `count`, `offset` and
    // `limit`, and the right pattern already exists in this repo:
    // `redact-app/state/modules/admin/services/queries.ts` passes offset and
    // limit and reads `count` back. This should do the same and keep fetching
    // until it has them all. `count` is selected below so the truncation is at
    // least visible in the network response while this stopgap stands.
    const query = `
      query GetRedactConfig($applicationId: ID!) {
        applicationConfig(appId: $applicationId, limit: 100){
          count
          offset
          limit
          records {
            configType
            configKey
            value
            valueJSON
            defaultValue
            defaultValueJSON
          }
        }
      }`;
    return await callGraphQLApi<ApplicationConfigsResponse>({
      actionTypes: [
        'noop',
        FETCH_APPLICATION_CONFIGS_SUCCESS,
        FETCH_APPLICATION_CONFIGS_FAILURE,
      ],
      query,
      variables: { applicationId },
      dispatch,
      getState,
      bailout: undefined,
    });
  };
